/**
 * The browser half of the Insights activity menu (components/site-analytics/activity-menu.blade.php; D96 of
 * documentation/pr-plans/insights-simplify-plan.md, P7): a disclosure over a list of links, as
 * accessibility.md §9 asks of navigation, built the way <x-insights.dropdown> is (insights/dropdown.js).
 * - The button opens the panel, and focus moves to the item on screen; Down or Up on the button opens it
 *   too, Up on the last item.
 * - Up and Down step through the items' links, stopping at either end; Home and End go to them.
 * - Escape closes it and returns focus to the button, and goes no further, so the pinned bar stays as it
 *   was; a press on an item closes it, and the page opens the item in place (insights-views.js).
 * - Tab past the last link, or Shift+Tab before the first, moves on and closes the panel behind it; a
 *   press outside closes it and leaves focus where the press put it. Not a focusout: Safari moves focus
 *   to <body> on a press on a link, which would close the panel under the pointer before its click landed.
 *
 * The panel is shown by a class, not x-show, which shows it a frame late: the focus sent into it as it
 * opens would land on a hidden link and fall to <body>.
 *
 * Mixed into the Actions view's scope (InsightsActions, insights-actions.js), where the teleported menu
 * still sits, under the page's views script's (`view`, insights-views.js): the two say which item is on
 * screen. Prefix: am_.
 */
export default function activityMenu() {
    return {
        am_open: false,

        /** The item on screen: Stats or Guides while one of them shows, else the open activity. */
        am_current() {
            return this.view === "stats" || this.view === "guides" ? this.view : this.$wire.activity;
        },

        am_toggle() {
            if (this.am_open) {
                this.am_close(true);

                return;
            }

            this.am_show();
        },

        /** Open, focus on the item on screen (else the first link), or the last one for Up. */
        am_show(fromEnd = false) {
            this.am_open = true;
            // The figures, counted again if something done since may have changed them (insights-actions.js).
            this.ia_countIfStale?.();
            this.$nextTick(() => {
                const links = this.am_links();
                const current = links.find((link) => link.getAttribute("aria-current") === "page");

                (fromEnd ? links.at(-1) : (current ?? links[0]))?.focus();
            });
        },

        /** Closed; focus goes back to the button only when a key closed it. */
        am_close(refocus = false) {
            if (!this.am_open) {
                return;
            }

            this.am_open = false;

            if (refocus) {
                this.$refs.amTrigger?.focus();
            }
        },

        /** Escape, from the button or the panel: closed, with focus on the button. */
        am_escape(event) {
            if (!this.am_open) {
                return;
            }

            event.stopPropagation();
            this.am_close(true);
        },

        /** Down or Up on the closed button opens the panel. */
        am_triggerKeydown(event) {
            if (this.am_open || (event.key !== "ArrowDown" && event.key !== "ArrowUp")) {
                return;
            }

            event.preventDefault();
            this.am_show(event.key === "ArrowUp");
        },

        /** Up, Down, Home and End move through the links; Tab past either end closes the panel. */
        am_keydown(event) {
            const links = this.am_links();
            const index = links.indexOf(document.activeElement);

            switch (event.key) {
                case "ArrowDown":
                case "ArrowUp":
                    event.preventDefault();
                    links[Math.min(links.length - 1, Math.max(0, index + (event.key === "ArrowDown" ? 1 : -1)))]?.focus();

                    return;
                case "Home":
                case "End":
                    event.preventDefault();
                    (event.key === "End" ? links.at(-1) : links[0])?.focus();

                    return;
                case "Tab":
                    this.am_tabOut(event);

                    return;
                default:
            }
        },

        /**
         * Tab past either end of the panel: focus moves on, and the panel closes behind it. A task later,
         * once the browser has moved focus: hidden first, the focused link would drop focus to <body>, and
         * Tab would move on from the top of the page.
         */
        am_tabOut(event) {
            // Every stop in the panel, the background work's link and × after the items among them.
            const stops = Array.from(this.$refs.amPanel?.querySelectorAll("a[href], button:not([disabled])") ?? []);
            const edge = event.shiftKey ? stops[0] : stops.at(-1);

            if (stops.length > 0 && document.activeElement !== edge) {
                return;
            }

            setTimeout(() => {
                this.am_open = false;
            }, 0);
        },

        /** The items' links, which Up, Down, Home and End move through. */
        am_links() {
            return Array.from(this.$refs.amPanel?.querySelectorAll("a[data-insights-activity-menu-item]") ?? []);
        },
    };
}
